/**
 * Lead Attribution shared module
 *
 * Single source of truth for the eight Google Ads + UTM tracking
 * params that flow from the URL → first-party cookie → form
 * submission → metwide-api, plus `referrer` (document.referrer
 * captured at landing, filtered to cross-origin values).
 *
 * Field names match the metwide-api validation rules exactly
 * (`HasLeadAttributionRules`); any other casing breaks validation.
 *
 * Importable from both Vue (app) and Nitro (server) sides via
 * `~~/shared/lead-attribution`.
 */

/** Cookie name. Greppable; do not invent variants elsewhere. */
export const ATTRIBUTION_COOKIE_NAME = 'metwide_attr'

/** 90 days in seconds. Matches Google's gclid lookback window. */
export const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90

/** Mirrors api `max:191` rule for the eight URL params. */
export const ATTRIBUTION_VALUE_MAX_LENGTH = 191

/** Mirrors api `max:2048` rule for the referrer field (stored as text). */
export const REFERRER_VALUE_MAX_LENGTH = 2048

/** Mirrors api `max:191` rule for the GA4 client id (`_ga` cookie).
 *  Google does not document a hard upper bound on the client id, so a
 *  read that returns more than this is omitted (not truncated): a
 *  truncated identifier is worse than a missing one because future GA4
 *  reconciliation would look up the wrong client. */
export const GA_CLIENT_ID_MAX_LENGTH = 191

/** Mirrors api `max:32` rule for the GA4 session id (`_ga_<measurementId>`). */
export const GA_SESSION_ID_MAX_LENGTH = 32

/** The eight URL-borne fields, in api-expected casing (snake_case for utm_*). */
export const LEAD_ATTRIBUTION_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export type LeadAttributionKey = (typeof LEAD_ATTRIBUTION_KEYS)[number]

/** Captured from `document.referrer`, not the URL. Separate so the query
 *  reader doesn't accidentally pick it up.
 */
export const LEAD_REFERRER_KEY = 'referrer' as const

/**
 * GA4 first-party identifiers read from the `_ga` / `_ga_<container>`
 * cookies. Separate from {@link LEAD_ATTRIBUTION_KEYS} because they
 * come from cookies, not the URL, and have their own length caps.
 */
export const GA4_FIELD_KEYS = ['ga_client_id', 'ga_session_id'] as const

export type Ga4FieldKey = (typeof GA4_FIELD_KEYS)[number]

/**
 * Every key forwarded to metwide-api: the eight URL params, referrer,
 * and the two GA4 identifiers. `pickAttributionFields` /
 * `useAttribution()` iterate this list so a new field only needs to be
 * added here once.
 */
export const LEAD_FORWARDED_KEYS = [
  ...LEAD_ATTRIBUTION_KEYS,
  LEAD_REFERRER_KEY,
  ...GA4_FIELD_KEYS,
] as const

export type LeadForwardedKey = (typeof LEAD_FORWARDED_KEYS)[number]

/** Flat object of any subset of the forwarded keys. Empty keys are omitted. */
export type LeadAttributionPayload = Partial<Record<LeadForwardedKey, string>>

/** Per-key length cap. Referrer holds long query strings; GA4 ids have
 *  their own caps; the eight URL params each fit in 191. Keep in sync
 *  with HasLeadAttributionRules.
 */
export function attributionMaxLength(key: LeadForwardedKey): number {
  switch (key) {
    case LEAD_REFERRER_KEY:
      return REFERRER_VALUE_MAX_LENGTH
    case 'ga_client_id':
      return GA_CLIENT_ID_MAX_LENGTH
    case 'ga_session_id':
      return GA_SESSION_ID_MAX_LENGTH
    default:
      return ATTRIBUTION_VALUE_MAX_LENGTH
  }
}

/**
 * Pull the eight known params off a URL search string (or pre-parsed
 * URLSearchParams), trim, cap to 191 chars, and drop empty values.
 * Used by the client plugin on landing AND by tests.
 */
export function readAttributionFromQuery(
  search: string | URLSearchParams,
): LeadAttributionPayload {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search
  const out: LeadAttributionPayload = {}
  for (const key of LEAD_ATTRIBUTION_KEYS) {
    const raw = params.get(key)
    if (raw == null) continue
    const trimmed = raw.trim()
    if (trimmed === '') continue
    out[key] = trimmed.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH)
  }
  return out
}

/** Google linker cookies → the click-ID key each one carries. */
const GCL_COOKIE_TO_KEY = {
  _gcl_aw: 'gclid',
  _gcl_gb: 'gbraid',
} as const satisfies Record<string, LeadAttributionKey>

/**
 * Recover click IDs from Google's Conversion Linker cookies.
 *
 * gtag.js mirrors the click ID it sees into a first-party cookie
 * (`_gcl_aw` for gclid, `_gcl_gb` for gbraid), shaped
 * `GCL.<unix-ts>.<id>`. This is a fallback for the rare case where the
 * click ID never reached `location.search` (redirect chain or a
 * query-stripping proxy ahead of us) but gtag still captured it from
 * the original navigation. Pass `document.cookie`.
 *
 * Returns only the keys it could recover, trimmed + capped like the
 * query-string path. Never returns UTM keys (the linker cookies don't
 * carry them).
 */
export function readClickIdsFromGclCookies(
  cookieString: string,
): LeadAttributionPayload {
  const out: LeadAttributionPayload = {}
  for (const [cookieName, key] of Object.entries(GCL_COOKIE_TO_KEY)) {
    const raw = readCookieValue(cookieString, cookieName)
    if (raw == null) continue
    const parts = raw.split('.')
    // Expect `GCL.<timestamp>.<id...>`; the id segment(s) hold the click ID.
    if (parts[0] !== 'GCL' || parts.length < 3) continue
    const id = parts.slice(2).join('.').trim()
    if (id === '') continue
    out[key] = id.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH)
  }
  return out
}

/** Read a single cookie value out of a `document.cookie` string. */
function readCookieValue(cookieString: string, name: string): string | null {
  for (const pair of cookieString.split(/;\s*/)) {
    const eq = pair.indexOf('=')
    if (eq === -1) continue
    if (pair.slice(0, eq) !== name) continue
    try {
      return decodeURIComponent(pair.slice(eq + 1))
    } catch {
      return pair.slice(eq + 1)
    }
  }
  return null
}

/**
 * Filter an arbitrary object down to the forwarded attribution keys
 * (eight URL params + referrer) with non-empty string values. Used by
 * BFFs to forward only the whitelisted attribution fields to the
 * upstream Laravel api.
 */
export function pickAttributionFields(
  source: Record<string, unknown> | null | undefined,
): LeadAttributionPayload {
  if (!source || typeof source !== 'object') return {}
  const out: LeadAttributionPayload = {}
  for (const key of LEAD_FORWARDED_KEYS) {
    const value = source[key]
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed === '') continue
    out[key] = trimmed.slice(0, attributionMaxLength(key))
  }
  return out
}

/**
 * Read GA4's first-party identifiers from `document.cookie`.
 *
 * - `_ga` carries the GA4 client id: `GA1.<scope>.<int>.<int>`. The
 *   last two dot-separated ints joined by a dot are the client id
 *   (e.g. `1234567890.1700000000`). It is sticky across sessions for
 *   the lifetime of the cookie.
 * - `_ga_<measurementId>` carries the GA4 session state. Two formats
 *   are observed in the wild:
 *     - **GS1** (position-based, legacy):
 *       `GS1.1.<sessionId>.<sessionNumber>.<engagement>.<lastInteractionTs>.<...>`.
 *       Session id is dot-segment index 2 (a unix timestamp).
 *     - **GS2** (label-based, newer rollouts):
 *       `GS2.1.s<sessionId>$o<n>$g<n>$t<lastInteractionTs>...`.
 *       Session id is the value after the `s` label, separated by `$`.
 *   Session id rolls over every 30 minutes of inactivity.
 *
 * Returns only the keys it could parse. Never throws on malformed
 * input. If `measurementId` is empty, the session-id read is silently
 * skipped (the runtime config may not be set in local dev).
 *
 * The `measurementId` parameter is the GA4 measurement id (shape
 * `G-XXXXXXXXXX`); the `G-` prefix is stripped internally to build the
 * cookie name. Do NOT pass a GTM container id (`GTM-XXXXXXX`); the GA4
 * session cookie name does not match the GTM container.
 */
export function readGa4IdsFromCookies(
  cookieString: string,
  measurementId: string,
): LeadAttributionPayload {
  const out: LeadAttributionPayload = {}

  const gaRaw = readCookieValue(cookieString, '_ga')
  if (gaRaw != null) {
    const parts = gaRaw.split('.')
    // Expect at least `GA1.<scope>.<int>.<int>`; the last two segments
    // joined with a dot are the client id. Anything shorter / mis-prefixed
    // is ignored rather than coerced. Oversize values (> GA_CLIENT_ID_MAX_LENGTH)
    // are dropped rather than truncated: a truncated stable identifier
    // is worse than a missing one.
    if (parts[0] === 'GA1' && parts.length >= 4) {
      const clientId = parts.slice(-2).join('.').trim()
      if (clientId !== '' && clientId.length <= GA_CLIENT_ID_MAX_LENGTH) {
        out.ga_client_id = clientId
      }
    }
  }

  if (measurementId !== '') {
    const sessionCookieName = `_ga_${stripGa4Prefix(measurementId)}`
    const sessionRaw = readCookieValue(cookieString, sessionCookieName)
    if (sessionRaw != null) {
      const sessionId = parseGa4SessionId(sessionRaw)
      if (sessionId != null && sessionId.length <= GA_SESSION_ID_MAX_LENGTH) {
        out.ga_session_id = sessionId
      }
    }
  }

  return out
}

/**
 * Parse the session id out of a `_ga_<measurementId>` cookie value.
 * Returns null if the value matches neither known GS1 nor GS2 format.
 */
function parseGa4SessionId(raw: string): string | null {
  const parts = raw.split('.')
  if (parts.length < 3) {
    return null
  }

  // GS1: `GS1.1.<sessionId>.<sessionNumber>.<engagement>.<lastInteractionTs>.<...>`.
  // Session id is at dot-segment index 2.
  if (parts[0] === 'GS1') {
    const sessionId = (parts[2] ?? '').trim()
    return sessionId === '' ? null : sessionId
  }

  // GS2: `GS2.1.s<sessionId>$o<n>$g<n>$t<lastInteractionTs>...`.
  // The session-state payload is in dot-segment index 2, with `$`-separated
  // labelled fields. We pull the `s<digits>` field by label so a future
  // reorder by Google does not break us. See
  // https://optimizesmart.com/blog/understanding-google-analytics-4-cookies-_ga-cookie/
  // for a worked example.
  if (parts[0] === 'GS2') {
    const payload = parts[2] ?? ''
    for (const field of payload.split('$')) {
      if (field.startsWith('s')) {
        const sessionId = field.slice(1).trim()
        return sessionId === '' ? null : sessionId
      }
    }
    return null
  }

  return null
}

/**
 * GA4 measurement IDs come through as `G-XXXXXXXX` but the cookie is
 * named `_ga_XXXXXXXX` (no `G-` prefix). Strip the prefix here so
 * callers can pass the runtime config value verbatim.
 */
function stripGa4Prefix(measurementId: string): string {
  return measurementId.startsWith('G-') ? measurementId.slice(2) : measurementId
}

/**
 * Normalise `document.referrer` for storage. Returns `{ referrer }` only
 * when the value is a non-empty, cross-origin URL; same-origin and empty
 * referrers carry no attribution signal and are dropped (otherwise an
 * internal navigation would overwrite the true external referrer).
 *
 * Unparseable referrers (rare; some browsers / extensions strip schemes)
 * are also dropped rather than guessed.
 */
export function readReferrer(
  referrer: string | null | undefined,
  currentOrigin: string,
): LeadAttributionPayload {
  if (typeof referrer !== 'string') return {}
  const trimmed = referrer.trim()
  if (trimmed === '') return {}
  let origin: string
  try {
    origin = new URL(trimmed).origin
  } catch {
    return {}
  }
  if (origin === currentOrigin) return {}
  return {
    [LEAD_REFERRER_KEY]: trimmed.slice(0, REFERRER_VALUE_MAX_LENGTH),
  }
}
